import { faCaretRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, {
  Context,
  Fragment,
  useContext,
  useEffect,
  useState,
} from 'react';
import AppDivisionContext from '../../contexts/AppDivisionContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import { dateFormat } from '../../utils';
import divisionApi from '../../utils/api/division';

export interface IBreadCrumbsProps {
  Link: any;
  to: string;
  inspection?: {
    project: {
      id: number;
      name: string;
    };
    created_date: string;
  };
  resource?: {
    resourceName: string;
    regex: RegExp;
    context?: Context<any>;
    hook?: any;
    rootTo: string;
    contextKey?: string;
  };
  url?: string;
}

const BreadCrumbs: React.FC<IBreadCrumbsProps> = ({
  Link,
  to,
  inspection,
  resource,
  url,
}) => {
  const appDivisionContext = useContext(AppDivisionContext);
  const [instance, setInstance] = useState<{ name: string }>({ name: '' });
  const context =
    resource && resource.context ? useContext(resource.context) : null;
  const hookData =
    resource && resource.hook ? resource.hook(parseInt(to)) : null;
  const projectContext = useContext(ProjectContext);

  useEffect(() => {
    let listener: any;
    if (resource && resource.resourceName === 'Areas') {
      const areaId = parseInt(to);
      if (!isNaN(areaId)) {
        divisionApi.areas
          .get(areaId)
          .then((area) => {
            setInstance({ name: area?.name || '' });
          })
          .catch(() => setInstance({ name: '' }));

        // Listen for live updates to area name
        listener = (e: any) => {
          const { areaId: updatedId, name } = e.detail || {};
          if (updatedId === areaId && typeof name === 'string') {
            setInstance({ name });
          }
        };
        window.addEventListener('areaNameUpdated', listener);
      }
    } else if (resource && context) {
      setInstance(resource.contextKey ? context[resource.contextKey] : null);
    } else if (resource && hookData) {
      setInstance(hookData.data);
    }

    return () => {
      if (listener) {
        window.removeEventListener('areaNameUpdated', listener);
      }
      setInstance({ name: '' });
    };
  }, [context, hookData, resource, to]);

  if (resource) {
    // Special handling for Areas: Division -> divName -> Areas -> areaName
    if (resource.resourceName === 'Areas') {
      const division = appDivisionContext.divisions.find(
        (div: { id: number; name: string }) =>
          div.id === Number(url?.split('/')[2])
      );

      return (
        <Fragment>
          <Link to={`/divisions`}>Division</Link>

          {division?.name && (
            <Fragment>
              <FontAwesomeIcon
                icon={faCaretRight}
                className='pad-left pad-right'
              />
              <Link to={`/divisions/${appDivisionContext.appDivisionId}`}>
                {division.name}
              </Link>
            </Fragment>
          )}

          <FontAwesomeIcon icon={faCaretRight} className='pad-left pad-right' />
          <Link
            to={`/divisions/${appDivisionContext.appDivisionId}/resources/areas`}
          >
            Area
          </Link>

          {instance?.name ? (
            <Fragment>
              <FontAwesomeIcon
                icon={faCaretRight}
                className='pad-left pad-right'
              />
              <Link
                to={`/divisions/${appDivisionContext.appDivisionId}${resource.rootTo}/${to}`}
              >
                {instance.name}
              </Link>
            </Fragment>
          ) : null}
        </Fragment>
      );
    }

    return (instance && instance.name && instance.name.length > 0) ||
      inspection ? (
      <Fragment>
        {!inspection && (
          <Fragment>
            <FontAwesomeIcon
              icon={faCaretRight}
              className='pad-left pad-right'
            />
            <Link
              to={`/divisions/${appDivisionContext.appDivisionId}${resource.rootTo}/${to}`}
            >
              {instance.name}
            </Link>
          </Fragment>
        )}

        {inspection && (
          <Fragment>
            <FontAwesomeIcon
              icon={faCaretRight}
              className='pad-left pad-right'
            />
            <Link
              to={`/divisions/${appDivisionContext.appDivisionId}${resource.rootTo}/${projectContext.project?.id}`}
            >
              {projectContext.project?.name}
            </Link>

            <FontAwesomeIcon
              icon={faCaretRight}
              className='pad-left pad-right'
            />
            <Link
              to={`/divisions/${appDivisionContext.appDivisionId}${resource.rootTo}/${projectContext.project?.id}/inspections`}
            >
              Inspections
            </Link>
            <FontAwesomeIcon
              icon={faCaretRight}
              className='pad-left pad-right'
            />
            <Link to={`/inspection/${to}`}>
              {moment.utc(inspection.created_date).format(dateFormat)}
            </Link>
          </Fragment>
        )}
      </Fragment>
    ) : null;
  } else {
    return null;
  }
};

export default BreadCrumbs;
